/**
 * Portal theme presentation helpers (docs/features/portal-theme). The theme itself is resolved on the server: the
 * layering rule, readability warnings and notice status all live in `portal_theme_module`, and the portal applies what
 * the read returns. What stays here is pure presentation the shells use: the readable text colour to put on a coloured
 * surface, and the words for where a value came from.
 */

import type { ThemeSource } from "../data/customer-api-types";

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((c) => c + c)
          .join("")
      : match[1];
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function luminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1 to 21), or null if either is not a hex colour. */
export function contrastRatio(a: string, b: string): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const DARK_TEXT = "#1f1f24";
export const LIGHT_TEXT = "#ffffff";

/** The readable text colour to put on a background: whichever of white or near-black contrasts more. */
export function readableTextOn(background: string): string {
  const onLight = contrastRatio(DARK_TEXT, background) ?? 0;
  const onDark = contrastRatio(LIGHT_TEXT, background) ?? 0;
  return onDark >= onLight ? LIGHT_TEXT : DARK_TEXT;
}

const SOURCE_KIND_TEXT: Record<ThemeSource["kind"], string> = {
  built_in: "Pollards default",
  base_theme: "From base theme",
  customer: "Set for this customer",
  team: "Set for this team",
};

/** "From base theme: Clean", "Set for this customer", "Set for this team", "Pollards default". */
export function themeSourceText(source: ThemeSource): string {
  const text = SOURCE_KIND_TEXT[source.kind];
  return source.kind === "base_theme" && source.label
    ? `${text}: ${source.label}`
    : text;
}
